// src/home/pages/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { listDashboardVideosAll, Video, getTrendingVideos, isShortVideo } from '../../lib/services/videoService';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrentUser } from '../../contexts/UserContext';

import SpotlightCarousel from '../components/SpotlightCarousel';
import ContinueWatching from '../components/ContinueWatching';
import ShortsPreview from '../components/ShortsPreview';
import DashboardRow from '../components/DashboardRow';
import LiveNow from '../../live/components/LiveNow';
import { useDashboardRows } from '../hooks/useDashboardRows';
import CategoryBar, { CATEGORY_KEYWORDS } from '../components/CategoryBar';

interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  isCreator: boolean;
}

interface SpotlightVideo {
  video: Video;
  reason: string;
}

// ─── Module-level feed cache (survives unmount/remount for instant back-nav) ──
interface FeedCache {
  feedVideos: Video[];
  trendingVideos: Video[];
  spotlightVideos: SpotlightVideo[];
  ts: number;
}
let _feedCache: FeedCache | null = null;
const FEED_CACHE_TTL = 5 * 60 * 1000; // 5 min stale-while-revalidate
const SCROLL_KEY = 'vb_dashboard_scrollY';

export default function Dashboard() {
  const { t } = useTranslation('home');
  const { t: tCommon } = useTranslation('common');
  const { currentUser, loading: userLoading } = useCurrentUser();
  // Initialize from module-level cache for instant render on back-nav
  const _cached = _feedCache && (Date.now() - _feedCache.ts) < FEED_CACHE_TTL;
  const [spotlightVideos, setSpotlightVideos] = useState<SpotlightVideo[]>(_cached ? _feedCache!.spotlightVideos : []);
  const [trendingVideos, setTrendingVideos] = useState<Video[]>(_cached ? _feedCache!.trendingVideos : []);
  const [feedVideos, setFeedVideos] = useState<Video[]>(_cached ? _feedCache!.feedVideos : []);
  const [contentLoading, setContentLoading] = useState(!_cached);
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  // Auth is already handled by ProtectedRoute wrapper - no redirect needed here

  // Derive userData from currentUser for backwards compatibility
  const userData: UserData | null = currentUser ? {
    firstName: currentUser.firstName || currentUser.displayName.split(' ')[0] || 'User',
    lastName: currentUser.lastName || currentUser.displayName.split(' ')[1] || '',
    email: currentUser.email || '',
    isCreator: currentUser.role === 'creator'
  } : null;

  const loading = userLoading || contentLoading;

  // Category filter
  const [activeCategory, setActiveCategory] = useState('all');

  // Filter feedVideos by active category
  const filteredFeedVideos = React.useMemo(() => {
    if (activeCategory === 'all') return feedVideos;
    if (activeCategory === 'live') return feedVideos.filter((v) => v.type === 'live');
    if (activeCategory === 'shorts') return feedVideos.filter((v) => v.type === 'short');
    const keywords = CATEGORY_KEYWORDS[activeCategory] || [];
    if (keywords.length === 0) return feedVideos;
    return feedVideos.filter((v) => {
      const haystack = `${v.title} ${v.channelName || ''}`.toLowerCase();
      return keywords.some((kw) => haystack.includes(kw));
    });
  }, [feedVideos, activeCategory]);

  // Dynamic row system — session-shuffled with size variety
  const dynamicRows = useDashboardRows(
    activeCategory === 'all' ? feedVideos : filteredFeedVideos,
    activeCategory === 'all' ? trendingVideos : filteredFeedVideos,
  );

  useEffect(() => {
    const pickSpotlight = async () => {
      if (!currentUser) {
        setContentLoading(false);
        return;
      }
      try {
        const cacheKey = `spotlight_arr_${currentUser.uid}`;
        const cache = localStorage.getItem(cacheKey);
        let hasFreshSpotlightCache = false;
        if (cache) {
          const { videos, ts } = JSON.parse(cache);
          if (Date.now() - ts < 24 * 60 * 60 * 1000) {
            setSpotlightVideos(videos);
            hasFreshSpotlightCache = true;
          }
        }
        // Always load feed rows even when spotlight cache is warm.
        const allVideos = await listDashboardVideosAll({ maxVideos: 2000 });
        setFeedVideos(allVideos);

        if (hasFreshSpotlightCache) {
          return;
        }
        // Fetch user watch history
        const histSnap = await getDoc(doc(db, 'userWatchHistory', currentUser.uid));
        let watched: { [id: string]: any } = {};
        if (histSnap.exists()) watched = histSnap.data().entries || {};
        // Fetch user search history
        let searchTerms: string[] = [];
        try {
          const searchSnap = await getDocs(query(collection(db, `userSearchHistory/${currentUser.uid}/queries`), orderBy('ts', 'desc')));
          searchTerms = searchSnap.docs.map(d => d.data().query.toLowerCase());
        } catch {
          // Search history fetch failed - continue without it
        }
        // Filter out shorts (type or 9:16 native)
        const nonShortVideos = allVideos.filter(v => !isShortVideo(v));
        // 1. Most watched
        const mostWatched = Object.entries(watched)
          .sort((a, b) => (b[1].totalWatched || 0) - (a[1].totalWatched || 0))
          .map(([id]) => nonShortVideos.find(v => v.id === id))
          .filter(Boolean) as Video[];
        // 2. Search match
        let searchMatch: Video[] = [];
        if (searchTerms.length) {
          for (const term of searchTerms.slice(0, 10)) {
            searchMatch = nonShortVideos.filter(v => v.title.toLowerCase().includes(term));
            if (searchMatch.length) break;
          }
        }
        // 3. Trending (real trending)
        const trendingVids = await getTrendingVideos(1, 10); // last 24h, top 10
        const trending = trendingVids.filter(v =>
          !mostWatched.some(mw => mw.id === v.id) &&
          !searchMatch.some(sm => sm.id === v.id)
        );
        // 4. Wildcard (random new or unseen)
        const unseen = nonShortVideos.filter(v => !watched[v.id]);
        const wildcard = unseen.length ? unseen[Math.floor(Math.random() * unseen.length)] : nonShortVideos[Math.floor(Math.random() * nonShortVideos.length)];
        // Compose picks with reasons
        const picks: SpotlightVideo[] = [];
        if (mostWatched[0]) picks.push({ video: mostWatched[0], reason: 'Your Most Watched' });
        if (searchMatch[0]) picks.push({ video: searchMatch[0], reason: `Because you searched: "${searchTerms[0]}"` });
        if (trending[0]) picks.push({ video: trending[0], reason: 'Trending Now' });
        if (wildcard) picks.push({ video: wildcard, reason: 'Discover Something New' });
        // Ensure 4 unique videos
        const unique = picks.filter((v, i, arr) => arr.findIndex(x => x.video.id === v.video.id) === i).slice(0, 4);
        setSpotlightVideos(unique);
        localStorage.setItem(cacheKey, JSON.stringify({ videos: unique, ts: Date.now() }));
      } catch (error) {
        console.error('Error loading spotlight:', error);
      } finally {
        setContentLoading(false);
      }
    };
    pickSpotlight();
    getTrendingVideos(1, 10).then(setTrendingVideos).catch(() => setTrendingVideos([]));
  }, [currentUser]);

  // Keep module-level cache in sync (for instant back-nav)
  useEffect(() => {
    if (feedVideos.length > 0 || trendingVideos.length > 0 || spotlightVideos.length > 0) {
      _feedCache = { feedVideos, trendingVideos, spotlightVideos, ts: Date.now() };
    }
  }, [feedVideos, trendingVideos, spotlightVideos]);

  // Save scroll position on unmount (restored on back-nav)
  useEffect(() => {
    return () => {
      sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    };
  }, []);

  // Restore scroll position once content is ready
  useEffect(() => {
    if (!loading) {
      const saved = sessionStorage.getItem(SCROLL_KEY);
      if (saved) {
        const y = parseInt(saved, 10);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => window.scrollTo(0, y));
        });
        sessionStorage.removeItem(SCROLL_KEY);
      }
    }
  }, [loading]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--vb-bg-primary)', color: 'var(--vb-text-primary)' }}
      >
        {tCommon('status.loading')}
      </div>
    );
  }

  return (
    <div
      data-testid="page-home"
      className="min-h-screen pb-16"
      style={{
        backgroundColor: 'var(--vb-bg-primary)',
        color: 'var(--vb-text-primary)',
        // Only show background image in dark mode
        ...(darkMode ? {
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px), url('/images/satin-phone-bg.png')",
          backgroundRepeat: 'repeat, no-repeat',
          backgroundSize: '30px 30px, cover',
          backgroundPosition: 'center center',
          backgroundAttachment: 'fixed',
        } : {}),
      }}
    >
      {/* Welcome */}
      <div className="text-xl font-semibold text-left px-6 pt-8" style={{ color: 'var(--vb-accent)' }}>
        {t('dashboard.welcome', { name: userData?.firstName })}
      </div>

      {/* Spotlight Carousel - Full width */}
      <div className="w-full px-6">
        <SpotlightCarousel videos={spotlightVideos} />
      </div>

      {/* Category bar — sticky below spotlight */}
      <div className="sticky top-0 z-30 w-full px-6 py-2" style={{ backgroundColor: 'var(--vb-bg-primary)' }}>
        <CategoryBar active={activeCategory} onChange={setActiveCategory} />
      </div>

      {/* Main Content — dynamic row system */}
      <main className="w-full px-6 space-y-10">
        {/* Continue Watching — always first (stable anchor) */}
        {activeCategory === 'all' && <ContinueWatching />}

        {/* Shorts Row */}
        {(activeCategory === 'all' || activeCategory === 'shorts') && <ShortsPreview />}

        {/* Dynamic rows — shuffled per session with L/M/S variety */}
        {activeCategory !== 'all' && filteredFeedVideos.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">
            <p className="text-lg font-medium">No videos found for this category</p>
            <button
              onClick={() => setActiveCategory('all')}
              className="mt-3 text-sm underline hover:text-white transition-colors"
            >
              Show all videos
            </button>
          </div>
        ) : (
          dynamicRows.map((row) => (
            <DashboardRow
              key={row.id}
              headingId={row.id}
              title={row.title}
              size={row.size}
              videos={row.videos}
            />
          ))
        )}

        {/* Live Now — always last */}
        <LiveNow />
      </main>
    </div>
  );
}
